import { setBlockType } from 'prosemirror-commands';
import { InputRule } from 'prosemirror-inputrules';
import { MarkSpec, MarkType, Node, NodeSpec, NodeType, Schema } from 'prosemirror-model';
import { liftListItem, wrapInList } from 'prosemirror-schema-list';
import { Command, EditorState, Plugin, Transaction } from 'prosemirror-state';
import { liftTarget } from 'prosemirror-transform';
import { findParentNode, findSelectedNodeOfType } from 'prosemirror-utils';
import { EditorView } from 'prosemirror-view';
import { ALL_NODES, Nodes } from './constants';
import { BaseMark } from './extensions/base/BaseMark';
import { BaseNode } from './extensions/base/BaseNode';
import { Extension } from './extensions/base/Extension';
import { defaultMarkdownParser } from './markdown/parser';
import { defaultMarkdownSerializer } from './markdown/serializer';
import { isList } from './queries/isList';
import { createSchema } from './schema';

export function chainTransactions(...commands: (Command | undefined)[]): Command {
  return (state, dispatch): boolean => {
    const dispatcher = (tr: Transaction): void => {
      state = state.apply(tr);
      dispatch?.(tr);
    };
    const last = commands.pop();
    commands.map((command) => command?.(state, dispatcher));
    return last !== undefined && last(state, dispatch);
  };
}

export function clearNodes(): Command {
  return (state, dispatch) => {
    const { tr } = state;
    const { selection } = tr;
    const { ranges } = selection;

    if (!dispatch) {
      return true;
    }

    ranges.forEach(({ $from, $to }) => {
      state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
        if (node.type.isText) {
          return;
        }

        const { doc, mapping } = tr;
        const $mappedFrom = doc.resolve(mapping.map(pos));
        const $mappedTo = doc.resolve(mapping.map(pos + node.nodeSize));
        const nodeRange = $mappedFrom.blockRange($mappedTo);

        if (!nodeRange) {
          return;
        }

        const targetLiftDepth = liftTarget(nodeRange);

        if (node.type.isTextblock) {
          const { defaultType } = $mappedFrom.parent.contentMatchAt($mappedFrom.index());

          tr.setNodeMarkup(nodeRange.start, defaultType);
        }

        if (targetLiftDepth || targetLiftDepth === 0) {
          tr.lift(nodeRange, targetLiftDepth);
        }
      });
    });

    dispatch(tr);
    return true;
  };
}

export function isNodeActive(
  type: NodeType,
  attrs: Record<string, unknown> = {},
  state: EditorState
): boolean {
  const node =
    findSelectedNodeOfType(type)(state.selection) ||
    findParentNode((n) => n.type === type)(state.selection);

  if (!Object.keys(attrs).length || !node) {
    return !!node;
  }

  return node.node.hasMarkup(type, { ...node.node.attrs, ...attrs });
}

export function isMarkActive(mark: MarkType, state: EditorState): boolean {
  const { from, $from, to, empty } = state.selection;
  return empty
    ? !!mark.isInSet(state.storedMarks || $from.marks())
    : state.doc.rangeHasMark(from, to, mark);
}

export function toggleBlockType(
  type: Nodes,
  toggleType: Nodes,
  attrs: Record<string, unknown> = {}
) {
  return (state: EditorState, dispatch: (tr: Transaction) => void) => {
    const isActive = isNodeActive(state.schema.nodes[type], attrs, state);

    if (isActive) {
      return setBlockType(state.schema.nodes[toggleType], attrs)(state, dispatch);
    }

    return setBlockType(state.schema.nodes[type], attrs)(state, dispatch);
  };
}

export function toggleList(listType: NodeType, itemType: NodeType): Command {
  return (state, dispatch) => {
    const { schema, selection } = state;
    const { $from, $to } = selection;
    const range = $from.blockRange($to);
    const { tr } = state;

    if (!range) {
      return false;
    }

    const parentList = findParentNode((node) => isList(node, schema))(selection);

    if (range.depth >= 1 && parentList && range.depth - parentList.depth <= 1) {
      if (parentList.node.type === listType) {
        return liftListItem(itemType)(state, dispatch);
      }

      const currentItemType = parentList.node.content.firstChild?.type;
      if (currentItemType && currentItemType !== itemType) {
        return chainTransactions(clearNodes(), wrapInList(listType))(state, dispatch);
      }

      if (isList(parentList.node, schema) && listType.validContent(parentList.node.content)) {
        tr.setNodeMarkup(parentList.pos, listType);

        dispatch?.(tr);
        return false;
      }
    }

    const canWrapInList = wrapInList(listType)(state);

    if (canWrapInList) {
      return wrapInList(listType)(state, dispatch);
    }

    return chainTransactions(clearNodes(), wrapInList(listType))(state, dispatch);
  };
}

export function getCaretPosition(view: EditorView) {
  let pos;
  try {
    const fromNode = view.state.selection.$from;
    const finalPos = fromNode.pos - fromNode.parentOffset;

    pos = view.coordsAtPos(finalPos);

    return {
      top: pos.top,
      bottom: pos.bottom,
      left: pos.left,
      right: pos.right,
    };
  } catch {
    return {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
  }
}

export function clearFloatingTrigger(view: EditorView, search: string, trigger: string) {
  const poss = view.state.doc.cut(
    view.state.selection.from - (search ?? '').length - trigger.length,
    view.state.selection.from
  );
  const trimTrigger = poss.textContent.startsWith(trigger);

  if (!search && !trimTrigger) {
    return;
  }

  // clear search input
  view.dispatch(
    view.state.tr.insertText(
      '',
      Math.max(
        0,
        view.state.selection.from - (search ?? '').length - (trimTrigger ? trigger.length : 0)
      ),
      view.state.selection.to
    )
  );
}

export function isNode(extension: Extension<void | NodeSpec | MarkSpec>): extension is BaseNode {
  return extension.type === 'node';
}

export function isMark(extension: Extension<void | NodeSpec | MarkSpec>): extension is BaseMark {
  return extension.type === 'mark';
}

export function getPluginsFromExtensions(
  extensions: Extension<void | NodeSpec | MarkSpec>[],
  schema: Schema
) {
  return extensions.reduce((acc, extension) => {
    return [...acc, ...extension.plugins({ schema })];
  }, [] as Plugin[]);
}

export function getInputRulesFromExtensions(
  extensions: Extension<void | NodeSpec | MarkSpec>[],
  schema: Schema
) {
  return extensions.reduce((acc, extension) => {
    return [...acc, ...extension.inputRules({ schema })];
  }, [] as InputRule[]);
}

export function getCommandsFromExtensions(
  extensions: Extension<void | NodeSpec | MarkSpec>[],
  schema: Schema
) {
  return extensions.reduce(
    (acc, extension) => {
      return { ...acc, ...extension.commands({ schema }) };
    },
    {} as Record<string, Command>
  );
}

export function parseContent(content: string) {
  try {
    return JSON.parse(content);
  } catch {
    const parsedDoc = defaultMarkdownParser.parse(content);
    return parsedDoc.toJSON();
  }
}

/**
 * Parse stored content (JSON or markdown) into a ProseMirror {@link Node} for the given schema.
 * For schemas that are a subset of the full editor, use {@link parseContentToDocSafe} instead.
 */
export function parseContentToDoc(content: string, schema: Schema): Node {
  return schema.nodeFromJSON(parseContent(content));
}

/**
 * Like {@link parseContentToDoc} but runs {@link convertDocForSchema} so content matches
 * schemas with fewer node types (e.g. simplified editors).
 */
export function parseContentToDocSafe(content: string, schema: Schema): Node {
  const parsedDoc = parseContent(content);
  const convertedDoc = convertDocForSchema(parsedDoc, schema);
  return schema.nodeFromJSON(convertedDoc);
}

/** @deprecated Use {@link parseContentToDocSafe} */
export function safeParseContentForSchema(content: string, schema: Schema): Node {
  return parseContentToDocSafe(content, schema);
}

type RawMark = {
  type: string;
};

type RawNode = {
  type: string;
  content?: RawNode[];
  text?: string;
  marks?: RawMark[];
  attrs?: Record<string, unknown>;
};

export function convertDocForSchema(doc: unknown, schema: Schema): unknown {
  if (typeof doc !== 'object' || doc === null) {
    return doc;
  }

  if (Array.isArray(doc)) {
    return doc.map((item) => convertDocForSchema(item, schema));
  }

  if ('type' in doc && 'content' in doc && doc.type === 'doc') {
    return {
      type: 'doc',
      content: (doc.content as RawNode[])?.flatMap((item) =>
        flattenDoc(item, schema, item.type === 'paragraph')
      ),
    };
  }

  return doc;
}

function flattenDoc(node: RawNode, schema: Schema, isParentParagraph: boolean): RawNode[] {
  if (!node) return [];

  const schemaNode = schema.nodes?.[node.type];

  if (node.marks) {
    node.marks = node.marks.filter((mark) => schema.marks?.[mark.type]);
  }

  if (!schemaNode) {
    const nodeType = ALL_NODES.find((n) => n.name === node.type);

    if (!nodeType) {
      return [];
    }

    if (node.type === 'bullet_list' || node.type === 'ordered_list') {
      // Flatten all list items
      return node.content?.flatMap((item) => flattenDoc(item, schema, isParentParagraph)) ?? [];
    }

    if (node.type === 'list_item') {
      // Each list_item may contain paragraphs and/or nested lists
      return node.content?.flatMap((child) => flattenDoc(child, schema, isParentParagraph)) ?? [];
    }

    // Leaf nodes (e.g. horizontal_rule, image, hard_break) have no extractable text,
    // so fall back to their own plain-text representation (the same one used to
    // render them as text elsewhere, e.g. `---` for a horizontal rule) rather than
    // dropping them silently.
    const leafText = nodeType.schema.leafText;
    const text = leafText
      ? leafText({ attrs: node.attrs ?? {} } as unknown as Node)
      : extractTextFromNode(node);

    if (!text) {
      return [];
    }

    return isParentParagraph
      ? [
          {
            type: 'text',
            text,
          },
        ]
      : [
          {
            type: 'paragraph',
            content: [
              {
                type: 'text',
                text,
              },
            ],
          },
        ];
  }

  if (node.content) {
    // Normal node with children
    return [
      {
        ...node,
        content:
          node.content?.flatMap((child) =>
            flattenDoc(child, schema, isParentParagraph || node.type === 'paragraph')
          ) ?? [],
      },
    ];
  }

  // Text or leaf node
  if (node.type === 'text' && !node.text) {
    return [];
  }

  return [node];
}

function extractTextFromNode(node: RawNode): string {
  let text = '';

  if (node.type === 'text' && node.text) {
    text += node.text;
  }

  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      text += extractTextFromNode(child);
    }
  }

  return text;
}

export default function textBetween(doc: Node, from: number, to: number): string {
  let text = '';
  let first = true;
  const blockSeparator = '\n\n';

  doc.nodesBetween(from, to, (node, pos) => {
    let nodeText = '';

    if (node.type.spec.leafText) {
      nodeText += node.type.spec.leafText(node);
    } else if (node.isText) {
      nodeText += node.textBetween(Math.max(from, pos) - pos, to - pos, blockSeparator);
    }

    if (node.isBlock && ((node.isLeaf && nodeText) || node.isTextblock) && blockSeparator) {
      if (first) {
        first = false;
      } else {
        text += blockSeparator;
      }
    }

    text += nodeText;
  });

  return text;
}

export function getContentAsString(content: string): string {
  return parseContentToDoc(content, createSchema()).textContent;
}

export function getContentAsMarkdown(content: string): string {
  return defaultMarkdownSerializer.serialize(parseContentToDoc(content, createSchema()));
}
